import { PieceDto } from "@growth/dto";
import { CONTENT_FORMATS, ContentFormat, PIECE_STATUS_LABELS, PieceStatus } from "@growth/schema";
import { Button } from "@growth/ui";
import { LoaderCircle, Plus } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ErrorText, selectClass } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { useAddVersion } from "../lib/calendar";

const day = (date: string) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString("en-AU", {
    timeZone: "UTC",
    weekday: "short",
    day: "numeric",
    month: "short",
  });

/** One idea, every platform: this piece's other versions, and making one for another platform. */
export const Versions = ({ piece }: { piece: PieceDto }) => {
  const { atLeast } = useActive();
  const navigate = useNavigate();
  const add = useAddVersion(piece.id);
  const taken = new Set([
    `${piece.channel}:${piece.format}`,
    ...piece.versions.map((v) => `${v.channel}:${v.format}`),
  ]);
  const options = (Object.keys(CONTENT_FORMATS) as ContentFormat[]).filter(
    (format) => !taken.has(format),
  );
  const [format, setFormat] = useState<ContentFormat | "">("");

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line pb-3 text-sm">
      {piece.due && (
        <span className="rounded-full bg-paper px-2.5 py-1 text-xs">
          On the calendar for {day(piece.due)}
        </span>
      )}
      <span className="rounded-lg border-2 border-accent bg-white px-2.5 py-1 text-xs font-medium">
        {CONTENT_FORMATS[`${piece.channel}:${piece.format}` as ContentFormat] ?? piece.channel}
      </span>
      {piece.versions.map((version) => (
        <Link
          key={version.id}
          to={`/content/${version.id}`}
          className="rounded-lg border border-line bg-white px-2.5 py-1 text-xs hover:border-accent"
        >
          {CONTENT_FORMATS[`${version.channel}:${version.format}` as ContentFormat] ??
            version.channel}
          <span className="text-muted">
            {" "}
            · {PIECE_STATUS_LABELS[version.status as PieceStatus] ?? version.status}
          </span>
        </Link>
      ))}
      {atLeast("EDITOR") && options.length > 0 && (
        <span className="flex items-center gap-1.5">
          <select
            aria-label="Make it for another platform"
            className={`${selectClass} h-8 w-56 text-xs`}
            value={format}
            onChange={(event) => setFormat(event.target.value as ContentFormat)}
          >
            <option value="">Make it for another platform…</option>
            {options.map((value) => (
              <option key={value} value={value}>
                {CONTENT_FORMATS[value]}
              </option>
            ))}
          </select>
          <Button
            variant="secondary"
            className="whitespace-nowrap"
            disabled={!format || add.isPending}
            onClick={() =>
              format &&
              add.mutate(format, { onSuccess: (created) => navigate(`/content/${created.id}`) })
            }
          >
            {add.isPending ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : (
              <Plus className="size-4" />
            )}
            Make it
          </Button>
        </span>
      )}
      {add.isError && <ErrorText>{apiErrorMessage(add.error)}</ErrorText>}
    </div>
  );
};
